import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import en, { MsgKey, Messages } from './en';
import ar from './ar';
import es from './es';
import pt from './pt';
import fr from './fr';
import de from './de';
import tr from './tr';
import ru from './ru';
import it from './it';
import pl from './pl';
import nl from './nl';

/**
 * Customer-facing translations for the tracker.
 *
 * Language choice, first match wins:
 *   1. ?lang=xx in the link (staff can send a link in the customer's language)
 *   2. what the customer picked last time (localStorage)
 *   3. the browser's language list
 *   4. English
 *
 * Every language is a Partial of en.ts: a missing key shows English, never a
 * blank. To add a language, add one file beside this one and register it below.
 */

export interface LangInfo { code: string; label: string; rtl?: boolean; }

const PACKS: Record<string, Partial<Messages>> = { en, ar, es, pt, fr, de, tr, ru, it, pl, nl };

export const LANGUAGES: LangInfo[] = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية', rtl: true },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ru', label: 'Русский' },
  { code: 'it', label: 'Italiano' },
  { code: 'pl', label: 'Polski' },
  { code: 'nl', label: 'Nederlands' },
];

const STORAGE_KEY = 'tc_lang';
const known = (c?: string | null) => (c && PACKS[c] ? c : null);
const base = (c: string) => c.toLowerCase().split(/[-_]/)[0];

export const detectLanguage = (): string => {
  try {
    const q = known(base(new URLSearchParams(window.location.search).get('lang') || ''));
    if (q) return q;
  } catch { /* no window / bad URL — fall through */ }
  try {
    const saved = known(window.localStorage.getItem(STORAGE_KEY));
    if (saved) return saved;
  } catch { /* storage blocked */ }
  try {
    for (const l of navigator.languages || [navigator.language]) {
      const hit = known(base(l || ''));
      if (hit) return hit;
    }
  } catch { /* ignore */ }
  return 'en';
};

type Vars = Record<string, string | number>;

const fill = (s: string, vars?: Vars) =>
  vars ? s.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m)) : s;

interface Ctx {
  lang: string;
  setLang: (c: string) => void;
  /** Plain string. */
  t: (key: MsgKey, vars?: Vars) => string;
  /** String with <b>/<em> runs turned into elements. */
  rich: (key: MsgKey, vars?: Vars) => React.ReactNode;
  /** List stored as newline-separated items. */
  list: (key: MsgKey, vars?: Vars) => string[];
  /** Same list, with markup rendered. */
  richList: (key: MsgKey, vars?: Vars) => React.ReactNode[];
  has: (key: string) => boolean;
}

const I18nContext = createContext<Ctx | null>(null);

const renderRich = (s: string): React.ReactNode => {
  const parts = s.split(/(<b>.*?<\/b>|<em>.*?<\/em>)/g).filter(p => p !== '');
  return parts.map((p, i) => {
    if (p.startsWith('<b>')) return <strong key={i}>{p.slice(3, -4)}</strong>;
    if (p.startsWith('<em>')) return <em key={i}>{p.slice(4, -5)}</em>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<string>(detectLanguage);

  const info = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

  // Keep <html lang/dir> truthful: it is what lets the browser's own
  // translate offer, screen readers, and RTL layout do the right thing.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = info.rtl ? 'rtl' : 'ltr';
  }, [lang, info.rtl]);

  const value = useMemo<Ctx>(() => {
    const pack = PACKS[lang] || {};
    const raw = (key: MsgKey): string => pack[key] ?? (en as Messages)[key] ?? String(key);
    return {
      lang,
      setLang: (c: string) => {
        const next = known(c) || 'en';
        setLangState(next);
        try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* storage blocked */ }
      },
      t: (key, vars) => fill(raw(key), vars),
      rich: (key, vars) => renderRich(fill(raw(key), vars)),
      list: (key, vars) => fill(raw(key), vars).split('\n').filter(Boolean),
      richList: (key, vars) => fill(raw(key), vars).split('\n').filter(Boolean).map(renderRich),
      has: (key) => key in (en as Messages),
    };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): Ctx => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
};

/** Compact language picker for the nav bar. */
export const LanguageSwitcher: React.FC = () => {
  const { lang, setLang, t } = useI18n();
  return (
    <label className="tc-lang" title={t('lang.label')}>
      <i className="fas fa-globe" aria-hidden="true"></i>
      <select
        className="tc-lang-select"
        value={lang}
        onChange={e => setLang(e.target.value)}
        aria-label={t('lang.label')}
      >
        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
    </label>
  );
};
